import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import MDTypography from "../../../../../components/MDTypography";
import MDBox from "../../../../../components/MDBox";
import { useHeadroomContext } from "../../context/HeadRoomProvider";
import DriverSelection from "./DriverSelection";
import { Column } from "../../HeadRoom";
import { cruxyColors } from "../../theme/color-palette";

interface AnalysisDriverCardProps {
  columns: Column[];
  isCollapsed?: boolean;
}

const AnalysisDriverCard: React.FC<AnalysisDriverCardProps> = ({ columns = [], isCollapsed = false }) => {
  const {
    driver1Type,
    driver2Type,
    driver1NumeratorType,
    driver2NumeratorType,
    driver1Mode,
    driver2Mode,
    handleDriver1Change,
    handleDriver2Change,
    handleDriver1NumeratorChange,
    handleDriver2NumeratorChange,
    handleDriver1ModeChange,
    handleDriver2ModeChange,
  } = useHeadroomContext();

  const [localIsCollapsed, setLocalIsCollapsed] = useState<boolean>(isCollapsed);

  useEffect(() => {
    setLocalIsCollapsed(isCollapsed);
  }, [isCollapsed]);

  const formatPercentageDisplay = (numerator: string, denominator: string) => {
    return `${numerator} as % of ${denominator}`;
  };

  return (
    <StyledCard isCollapsed={localIsCollapsed}>
      <MDBox display="flex" justifyContent="flex-start" alignItems="center" p={0.5}>
        <MDTypography variant="h6">Analysis Drivers</MDTypography>
      </MDBox>
      {localIsCollapsed ? null : (
        <MDBox p={1} pt={0}>
          <DriverSelection
            driverNumber={1}
            driverType={driver1Type}
            numeratorType={driver1NumeratorType}
            driverMode={driver1Mode}
            columns={columns}
            handleDriverChange={handleDriver1Change}
            handleNumeratorChange={handleDriver1NumeratorChange}
            handleDriverModeChange={handleDriver1ModeChange}
            formatPercentageDisplay={formatPercentageDisplay}
          />
          <DriverSelection
            driverNumber={2}
            driverType={driver2Type}
            numeratorType={driver2NumeratorType}
            driverMode={driver2Mode}
            columns={columns}
            handleDriverChange={handleDriver2Change}
            handleNumeratorChange={handleDriver2NumeratorChange}
            handleDriverModeChange={handleDriver2ModeChange}
            formatPercentageDisplay={formatPercentageDisplay}
          />
          <DriverBox>
            <MDTypography variant="caption" fontWeight="bold" mb={0.5}>
              Selected Drivers:
            </MDTypography>
            {!driver1Type && !driver2Type ? (
              <MDTypography variant="caption" color="text" fontSize="0.75rem">
                {" "}
                No drivers selected
              </MDTypography>
            ) : (
              <MDBox display="flex" flexDirection="column">
                <MDTypography variant="caption" color="text" fontSize="0.75rem" mb={0.25}>
                  <span style={{ fontWeight: 400 }}>Driver 1:</span>{" "}
                  {driver1Mode === "PERCENTAGE"
                    ? formatPercentageDisplay(driver1NumeratorType || "None", driver1Type || "None")
                    : driver1Type || "None"}
                </MDTypography>
                <MDTypography variant="caption" color="text" fontSize="0.75rem">
                  <span style={{ fontWeight: 400 }}>Driver 2:</span>{" "}
                  {driver2Mode === "PERCENTAGE"
                    ? formatPercentageDisplay(driver2NumeratorType || "None", driver2Type || "None")
                    : driver2Type || "None"}
                </MDTypography>
              </MDBox>
            )}
          </DriverBox>
        </MDBox>
      )}
    </StyledCard>
  );
};

const StyledCard = styled(MDBox, {
  shouldForwardProp: (prop) => prop !== "isCollapsed",
})<{ isCollapsed: boolean }>(({ theme, isCollapsed }) => ({
  height: isCollapsed ? "auto" : "360px",
  maxHeight: "360px",
  overflowY: isCollapsed ? "hidden" : "auto",
  padding: isCollapsed ? theme.spacing(0) : theme.spacing(1),
  borderRadius: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[3],
  transition: "height 0.3s ease, padding 0.3s ease",
}));

export const DriverBox = styled(MDBox)(({ theme }) => ({
  border: `1px solid ${cruxyColors.amber.main}`,
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(1),
  backgroundColor: theme.palette.background.default,
  transition: "box-shadow 0.3s ease",
  "&:hover": {
    boxShadow: theme.shadows[1],
  },
}));

export default React.memo(AnalysisDriverCard);
